import * as React from "react";
import { Expand, MapPin } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { TextLink } from "@/components/ui/TextLink";
import { cn } from "@/lib/cn";

/**
 * The arrival fix as a picture: a small map under the Location fact, and a
 * modal with the full view and a way out to a real maps app.
 *
 * ── OpenStreetMap tiles, composed by hand ───────────────────────────────────
 *
 * There is no map library here on purpose. This product needs "a static map
 * around one pin" twice, and a mapping dependency restates the design system in
 * its own idiom the same way the vendored pickers would have (console-ui.md).
 * A slippy-map tile is just an image at a computable URL, so the frame renders
 * the handful of standard OSM tiles covering the point and clips them - no
 * key, no SDK, and the tile origin is named in the console's CSP.
 *
 * The tiles are third-party content, so the frame treats them as optional: a
 * tile that fails to load leaves the neutral ground behind it, and everything
 * the record *asserts* - address, coordinates - is text outside this component.
 *
 * ── The pin means "here", not severity ──────────────────────────────────────
 *
 * It borrows the `stop` red because a red map pin is the convention every maps
 * app taught, but it carries no word and no state - the severity chips beside
 * it do.
 */

const TILE_SIZE = 256;
const TILE_ORIGIN = "https://tile.openstreetmap.org";

interface Tile {
  key: string;
  url: string;
  /** Offset of the tile's top-left from the fix, in CSS pixels. */
  left: number;
  top: number;
}

/**
 * The standard Web-Mercator tiles covering a `width` x `height` window centred
 * on the fix. Computed against the window's own size so a thumbnail fetches
 * two or three tiles rather than a speculative grid of nine.
 */
function tilesAround(lat: number, lng: number, zoom: number, width: number, height: number) {
  const n = 2 ** zoom;
  const xf = ((lng + 180) / 360) * n;
  const latRad = (lat * Math.PI) / 180;
  const yf = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n;

  const centerX = xf * TILE_SIZE;
  const centerY = yf * TILE_SIZE;

  const tiles: Tile[] = [];
  const firstX = Math.floor((centerX - width / 2) / TILE_SIZE);
  const lastX = Math.floor((centerX + width / 2) / TILE_SIZE);
  const firstY = Math.floor((centerY - height / 2) / TILE_SIZE);
  const lastY = Math.floor((centerY + height / 2) / TILE_SIZE);

  for (let x = firstX; x <= lastX; x += 1) {
    for (let y = firstY; y <= lastY; y += 1) {
      if (y < 0 || y >= n) continue;
      const wrappedX = ((x % n) + n) % n;
      tiles.push({
        key: `${zoom}/${x}/${y}`,
        url: `${TILE_ORIGIN}/${zoom}/${wrappedX}/${y}.png`,
        left: x * TILE_SIZE - centerX,
        top: y * TILE_SIZE - centerY,
      });
    }
  }
  return tiles;
}

function TileMap({
  lat,
  lng,
  zoom,
  width,
  height,
}: {
  lat: number;
  lng: number;
  zoom: number;
  /**
   * The largest window the frame can be, in CSS pixels. Only used to decide
   * which tiles to fetch - the frame itself is fluid and clips.
   */
  width: number;
  height: number;
}) {
  const tiles = React.useMemo(
    () => tilesAround(lat, lng, zoom, width, height),
    [lat, lng, zoom, width, height]
  );

  return (
    <div className="tile-map" aria-hidden>
      {tiles.map((tile) => (
        <img
          key={tile.key}
          src={tile.url}
          alt=""
          loading="lazy"
          decoding="async"
          draggable={false}
          className="tile-map__tile"
          // Computed per tile per fix - the sanctioned use of style={{}}.
          style={{ left: `calc(50% + ${tile.left}px)`, top: `calc(50% + ${tile.top}px)` }}
        />
      ))}
      <MapPin className="tile-map__pin" />
    </div>
  );
}

export function LocationPreview({
  lat,
  lng,
  address,
  plannedAddress,
  className,
}: {
  lat: number;
  lng: number;
  /** The reverse-geocoded arrival address, when the API could say. */
  address: string | null;
  /** What the appointment planned, for reading the two side by side. */
  plannedAddress: string | null;
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);

  const coords = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  const googleUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  const appleUrl = `https://maps.apple.com/?ll=${lat},${lng}&q=${encodeURIComponent("Visit arrival")}`;

  return (
    <>
      <button
        type="button"
        className={cn("map-thumb", className)}
        aria-haspopup="dialog"
        aria-label="Open the arrival location on a map"
        onClick={() => setOpen(true)}
      >
        <TileMap lat={lat} lng={lng} zoom={15} width={280} height={140} />
        <span className="map-thumb__hint">
          <Expand aria-hidden /> Map
        </span>
      </button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Arrival location"
        className="map-modal"
        footer={
          <div className="map-modal__actions">
            <TextLink href={googleUrl} external>
              Open in Google Maps
            </TextLink>
            <TextLink href={appleUrl} external>
              Open in Apple Maps
            </TextLink>
          </div>
        }
      >
        <div className="map-modal__map">
          <TileMap lat={lat} lng={lng} zoom={16} width={640} height={320} />
        </div>

        <dl className="map-modal__facts">
          <div className="map-modal__fact">
            <dt className="summary__fact-label">Where the phone was</dt>
            <dd className="summary__fact-value">{address ?? "No address for this fix"}</dd>
            <dd className="summary__fact-note mono tnum">{coords}</dd>
          </div>
          <div className="map-modal__fact">
            <dt className="summary__fact-label">Planned address</dt>
            <dd className="summary__fact-value">
              {plannedAddress ?? "No address on the appointment"}
            </dd>
          </div>
        </dl>
      </Modal>
    </>
  );
}
